export type SameSite = 'Strict' | 'Lax' | 'None';

/**
 * Check if a cookie exists by name
 */
export function doesCookieExist(name: string): boolean {
  try {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i]!.trim();
      if (cookie.startsWith(`${name}=`)) {
        return true;
      }
    }
  } catch (err) {
    window.$app.logger.warn(`Error checking cookie '${name}' existence:`, err);
  }
  return false;
}

/**
 * Get the value of a cookie by name
 */
export function getCookie(name: string): string | null {
  return (
    document.cookie
      .split('; ')
      .map((cookie) => cookie.split('='))
      .find(([key]) => key === name)?.[1] ?? null
  );
}

/**
 * Set a cookie
 * @returns true if the cookie was set successfully, false otherwise
 */
export function setCookie(name: string, value: string, domain: string, days?: number): boolean {
  let cookieString = `${name}=${value}; path=/; domain=${domain}; SameSite=Lax; Secure`;
  if (days !== undefined) {
    const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
    cookieString += `; expires=${expires}`;
  }
  // biome-ignore lint/suspicious/noDocumentCookie: Browser support
  document.cookie = cookieString;
  return doesCookieExist(name);
}

/**
 * Set a session cookie
 * As it's a session cookie, the expiration date is omitted,
 * as it will be deleted when the browser closes.
 * @returns true if the cookie was set successfully, false otherwise
 */
export function setSessionCookie(name: string, value: string, domain: string): boolean {
  return setCookie(name, value, domain);
}

/**
 * Delete a cookie
 * @returns true if the cookie was deleted successfully, false otherwise
 */
export function deleteCookie(
  name: string,
  path = '/',
  domain?: string,
  sameSite: SameSite = 'Lax',
  secure = true,
): boolean {
  if (!doesCookieExist(name)) {
    return false; // Cookie doesn't exist
  }

  let cookieString = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}`;

  // Add SameSite attribute
  if (sameSite === 'None') {
    cookieString += '; SameSite=None';
  } else if (sameSite === 'Lax') {
    cookieString += '; SameSite=Lax';
  } else if (sameSite === 'Strict') {
    cookieString += '; SameSite=Strict';
  }

  // Add Secure attribute if needed
  if (secure) {
    cookieString += '; Secure';
  }

  if (domain) {
    cookieString += `; domain=${domain}`;
  }

  // biome-ignore lint/suspicious/noDocumentCookie: Browser support
  document.cookie = cookieString;
  return !doesCookieExist(name); // Return true if the cookie was successfully deleted
}

/**
 * Write the cross-app theme cookie if it does not already exist.
 *
 * Sports and other Tipico apps read this cookie to align their theme
 * selection. We only write — never read — so we don't override the
 * user's choice in this app with a value set elsewhere.
 *
 * Cookie spec (IGM-2910):
 *   name:    theme
 *   value:   tipico-light | tipico-dark
 *   domain:  .tipico.de
 *   path:    /
 *   expires: 1 year
 */
const THEME_COOKIE_NAME = 'theme';
const THEME_COOKIE_DOMAIN = '.tipico.de';
const THEME_COOKIE_EXPIRY_DAYS = 365;

export function writeThemeCookieIfAbsent(appTheme: 'dark' | 'light'): void {
  if (doesCookieExist(THEME_COOKIE_NAME)) {
    window.$app.logger.log('Theme cookie already exists, skipping write');
    return;
  }
  const cookieValue = appTheme === 'light' ? 'tipico-light' : 'tipico-dark';
  const success = setCookie(THEME_COOKIE_NAME, cookieValue, THEME_COOKIE_DOMAIN, THEME_COOKIE_EXPIRY_DAYS);
  if (success) {
    window.$app.logger.log(`Theme cookie written: ${cookieValue}`);
  } else {
    window.$app.logger.log(`Theme cookie could not be written (domain: ${THEME_COOKIE_DOMAIN})`);
  }
}

/**
 * Get the base domain (TLD+1) for setting cookies.
 */
export function getBaseDomain(): string {
  const hostname = window.location.hostname;
  const parts = hostname.split('.');

  if (parts.length <= 2) {
    // 'localhost', 'example.com'
    return hostname;
  }

  // Will return TLD+1 (e.g. 'example.com' for 'www.example.com')
  return parts.slice(-2).join('.');
}
